import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  ComponentFactoryResolver,
  ViewContainerRef, ComponentRef, ViewChild, AfterContentInit, SimpleChanges, OnChanges
} from '@angular/core';
import { TemplateParserComponent } from '../template-parser/template-parser.component';
import { WorkflowBarItem } from '@ispm/shared/store/shared.reducer';
import {ModalTypes} from "@ispm/layout/components/modals/contants/modal-types.enum";
import {ConfirmModalComponent} from "@ispm/layout/components/modals/confirm-modal/confirm-modal.component";
import {ConfirmModalLoaderPayload} from "@ispm/layout/classes/confirm-modal-loader-payload";
import {DynamicModalLoaderService} from "@ispm/layout/services/dynamic-modal-loader.service";
import {DatePipe} from "@angular/common";
import { find, get, includes } from 'lodash';

@Component({
  selector: 'gen-workflow-step',
  templateUrl: './workflow-step.component.html',
  styleUrls: ['./workflow-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowStepComponent implements OnInit, OnChanges, AfterContentInit {
  @Input() stepItem: WorkflowBarItem;
  @Input() activeIndex: number;
  @Input() currentIndex: number;
  @Input() revision: any;
  @Input() stepTaglineField: any;
  @Input() testElapsedTime: number;
  @ViewChild('parser', { static: true, read: ViewContainerRef}) entry: ViewContainerRef;
  private componentInstance: ComponentRef<TemplateParserComponent>;
  public tooltipContent: string;
  public hasElapsed = false;
  public alertRequired: boolean = false;
  public pastDueDate: boolean = false;

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private modalLoader: DynamicModalLoaderService) {}

  public ngOnInit() {
    this.parseTemplate(this.stepItem.tooltip);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.stepItem) {
      this.setHasElapsed();
      this.dueDateEvaluation();
    }
  }

  /*
    parse payload

    {
    * 	"template": "checkFileExisting",
    * 	"payload"{
    * 		"pending": [
      *            { "filename":"We_Want_To_Wait_This_T_L001_R2_001.fastq.gz"  },
    *            { "filename":"Lets_wait.txt" }
    * 		],
    * 		"ready":[
      *            {
        * 				"filename":"231_T_L001_R1_001.fastq.gz",
        * 				"created":"2018-07-13T15:57:48.206"
        *            },
    *            {
      * 				"filename":"YYYYYY_L001_R1_001.fastq.gz",
      * 				"created":"2018-07-13T15:57:48.206"
        *            }
    * 		]
    * 	}
  * } */
  public showLastUpdatedDate: boolean;


  public compileTemplate(template, data, component = TemplateParserComponent) {
    if (!this.componentInstance) {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
      this.componentInstance = this.entry.createComponent(componentFactory);
    }
    (<TemplateParserComponent> this.componentInstance.instance).setData(data, template);
    this.componentInstance.hostView.detectChanges();
    return this.componentInstance.location.nativeElement.innerHTML;
  }


  private parseTemplate(value, type?: string) {
    if (!value) { return; }
    let toolTipObj;
    try {
      toolTipObj = JSON.parse(value);
      this.tooltipContent = this.compileTemplate(toolTipObj.template, JSON.parse(toolTipObj.payload));
    } catch (e) {
      this.tooltipContent = this.getHtml(value, type);
    }

  }

  public getHtml(value, type = 'sup') {
    if (!value) { return; }
    const regex = /([\d]+)((nd)|(rd)|(st)|(th))/g;
    let pattern;

    switch (type.toLowerCase()) {
      case 'sup':
        pattern = '$1<sup>$2</sup>';
        break;
    }
    return value.replace(regex, pattern).replace(/-/g, '\u2011');
  }

  public ngAfterContentInit(): void {
    this.parseTemplate(this.stepItem.tooltip);
  }

  public dueDateEvaluation() {
    let dueDateAlert = {};
    if(get(this.revision, 'items')) {
      this.revision.items.forEach(item => {
        if (find(item.inputs, ['key', 'calculateStatusAndDueDate'])) {
          item.inputs.forEach((attribute: any) => {
            if (get(attribute, 'key') === 'calculateStatusAndDueDate') {
              // Remove all occurrences of "undefined" (with or without quotes) from the JSON string
              let attributeValue = attribute.value.replace(/"undefined"|undefined/g, '""');
              attributeValue = JSON.parse(attributeValue);
              dueDateAlert = get(attributeValue, ['0', 'dueDateAlert']);
            }
          });
        }
      });
    }

    const today = new Date();
    this.alertRequired = get(dueDateAlert, 'alertRequired');
    if(this.alertRequired) {
      let status = get(dueDateAlert, 'status');
      if (status) {
        status = status.split(',');
        status = status.map(item => item.trim());
      }

      const datePipe = new DatePipe('en-US');
      this.pastDueDate = false;
      const dueDate=new Date(this.revision.dueDate);
      if(datePipe.transform(today, 'yyyy MMM dd')== datePipe.transform(this.revision.dueDate, 'yyyy MMM dd'))
      dueDate.setDate(dueDate.getDate()+1);
      if (includes(status, this.revision['status'])
        && this.revision['dueDate']
        && (today.getTime()>dueDate.getTime())) {
        this.pastDueDate = true;
        this.showDueDateAlert(this.revision.dueDate, get(dueDateAlert, 'message'), get(dueDateAlert, 'dateFormat'));
      }
      else{
        this.showLastUpdatedDate = true;//If today's date is less than duedate no pop will show
      }
    }
  }

  private setHasElapsed() {
    this.hasElapsed = false;

    if (!this.stepItem ||
        !this.testElapsedTime ||
        !this.revision || !this.revision.meta || !this.revision.meta.lastUpdated) { return; }

    const now = new Date();
    const revisionDate = new Date(this.revision.meta.lastUpdated);
    const timeDelta = now.getTime() - revisionDate.getTime();
    const daysInMs = this.testElapsedTime * 24 * 60 * 60 * 1000;
    this.hasElapsed = timeDelta > daysInMs;
    if(get(this.revision, 'items')) {
      this.revision.items.forEach(item => {
        if (find(item.inputs, ['key', 'showLastUpdatedDate'])) {
          this.showLastUpdatedDate=true;
        }
      });
    }
  }

  private showDueDateAlert(date, message, dateFormat = 'd MMM yyyy' ) {
    const datePipe = new DatePipe('en-US');
    const dueDate = datePipe.transform(date, dateFormat);
    const displayMessage = message.replace('${dueDate}',dueDate);
    this.modalLoader.popupModal(ConfirmModalComponent,
      new ConfirmModalLoaderPayload(
        '',
        displayMessage, '', 'OK').setBackdropAllowed(false),
      ModalTypes.ALERT)
      .subscribe(res => {
        // this.reset = true;
      });
  }
}
